import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { AuthSplitLayout } from "@/components/auth/AuthSplitLayout";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { ApiError } from "@/lib/api/client";
import {
  OTP_LENGTH,
  OTP_RESEND_SECONDS,
  resetPasswordSchema,
  zodFieldErrors,
} from "@/lib/auth-validations";
import { useCountdown } from "@/lib/use-countdown";
import { requestPasswordReset, verifyPasswordReset } from "@/services/auth";

type ResetLocationState = {
  email?: string;
  notice?: string;
};

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as ResetLocationState | null;
  const email = locationState?.email?.trim().toLowerCase() ?? "";
  const notice = locationState?.notice?.trim() || null;

  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const { seconds, start } = useCountdown(OTP_RESEND_SECONDS);

  useEffect(() => {
    if (!email) {
      navigate("/forgot-password", { replace: true });
    }
  }, [email, navigate]);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setInfo(null);

    const parsed = resetPasswordSchema.safeParse({ code, password, confirmPassword });
    if (!parsed.success) {
      setFieldErrors(zodFieldErrors(parsed.error));
      return;
    }

    setFieldErrors({});
    setLoading(true);
    try {
      await verifyPasswordReset({
        email,
        code: parsed.data.code,
        password: parsed.data.password,
      });
      navigate("/login", {
        replace: true,
        state: { notice: "Password updated. Sign in with your new password." },
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        const next: Record<string, string> = {};
        for (const [key, value] of Object.entries(err.fieldErrors)) {
          next[key] = Array.isArray(value) ? value[0] : value;
        }
        setFieldErrors(next);
      } else {
        setError("Unable to reset password");
      }
    } finally {
      setLoading(false);
    }
  };

  const onResend = async () => {
    if (seconds > 0 || resending) return;
    setError(null);
    setInfo(null);
    setResending(true);
    try {
      const message = await requestPasswordReset({ email });
      setInfo(message);
      start(OTP_RESEND_SECONDS);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Unable to resend the code");
    } finally {
      setResending(false);
    }
  };

  if (!email) return null;

  return (
    <AuthSplitLayout
      title="Reset password"
      description={`Enter the ${OTP_LENGTH}-digit code sent to ${email}, then choose a new password.`}
    >
      <form onSubmit={onSubmit} className="mt-6 space-y-4" noValidate autoComplete="off">
        {notice ? <Alert tone="info">{notice}</Alert> : null}
        {error ? <Alert>{error}</Alert> : null}
        {info ? <Alert tone="success">{info}</Alert> : null}
        <Input
          label="Email OTP"
          name="code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={OTP_LENGTH}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, OTP_LENGTH))}
          error={fieldErrors.code}
          placeholder={"0".repeat(OTP_LENGTH)}
        />
        {seconds > 0 ? (
          <p className="text-sm text-muted">Resend code in {seconds}s</p>
        ) : (
          <button
            type="button"
            className="text-sm font-medium text-brand hover:text-brand-strong disabled:opacity-50"
            onClick={() => void onResend()}
            disabled={resending}
          >
            {resending ? "Sending…" : "Resend OTP"}
          </button>
        )}
        <Input
          label="New password"
          name="password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={fieldErrors.password}
          hint="At least 6 characters, with upper, lower, number, and special character."
        />
        <Input
          label="Confirm password"
          name="confirmPassword"
          type="password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          error={fieldErrors.confirmPassword}
        />
        <Button type="submit" className="w-full" loading={loading}>
          Reset password
        </Button>
        <p className="text-center text-sm text-muted">
          <Link to="/login" className="font-medium text-brand hover:text-brand-strong">
            Back to sign in
          </Link>
        </p>
      </form>
    </AuthSplitLayout>
  );
}
